/**
 * Admin Dashboard - Main Page
 * Displays system health overview and operation monitoring
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useOverviewStats, useTenderLinks, useAdminParcels, useTenderLinkDetail, useParcelDetail, useSources } from '../../hooks/useAdminData';
import { triggerSourceCrawl, triggerAllCrawl } from '../../services/adminApi';
import { OverviewPanel } from './OverviewPanel';
import { FilterPanel } from './FilterPanel';
import { TenderLinksTable } from './TenderLinksTable';
import { ParcelsTable } from './ParcelsTable';
import { SourcesTable } from './SourcesTable';
import { CreateSourceForm } from './CreateSourceForm';
import { DetailModal } from './DetailModal';
import type { JobType, AdminFilters } from '@tender-monitor/shared/types/admin';
import './admin.css';

// Parse URL query params for filter state
function getFiltersFromUrl(): {
  jobType: JobType;
  status: string;
  startDate: string;
  endDate: string;
  page: number;
} {
  const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
  return {
    jobType: (params.get('type') as JobType) || 'tender-links',
    status: params.get('status') || '',
    startDate: params.get('startDate') || '',
    endDate: params.get('endDate') || '',
    page: parseInt(params.get('page') || '1', 10),
  };
}

// Update URL query params without full navigation
function updateUrl(filters: {
  jobType: JobType;
  status: string;
  startDate: string;
  endDate: string;
  page: number;
}) {
  const params = new URLSearchParams();
  params.set('type', filters.jobType);
  if (filters.status) params.set('status', filters.status);
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  if (filters.page > 1) params.set('page', String(filters.page));

  const newHash = `#/admin?${params.toString()}`;
  if (window.location.hash !== newHash) {
    window.history.replaceState(null, '', newHash);
  }
}

export function AdminDashboard() {
  // Overview data
  const { data: overview, loading: overviewLoading, error: overviewError, reload: reloadOverview } = useOverviewStats();

  // Filter state from URL
  const [jobType, setJobType] = useState<JobType>('tender-links');
  const [status, setStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);

  // Selected item for detail modal
  const [selectedTenderLinkId, setSelectedTenderLinkId] = useState<string | null>(null);
  const [selectedParcelId, setSelectedParcelId] = useState<string | null>(null);

  // Fetch detail data when item is selected
  const {
    data: tenderLinkDetail,
    loading: tenderLinkDetailLoading,
    error: tenderLinkDetailError,
  } = useTenderLinkDetail(selectedTenderLinkId);

  const {
    data: parcelDetail,
    loading: parcelDetailLoading,
    error: parcelDetailError,
  } = useParcelDetail(selectedParcelId);

  // Sources data
  const {
    data: sourcesData,
    loading: sourcesLoading,
    error: sourcesError,
    reload: reloadSources,
    toggle: toggleSource,
    togglingIds: togglingSourceIds,
  } = useSources();

  // Crawl state management with localStorage persistence
  const STORAGE_KEY = 'admin_crawling_sources';

  const [crawlingSourceIds, setCrawlingSourceIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Check if any stored crawls are older than 2 minutes (likely stale)
        const now = Date.now();
        const validIds = Object.entries(parsed)
          .filter(([_, timestamp]) => now - (timestamp as number) < 120000)
          .map(([id]) => id);
        return new Set(validIds);
      }
    } catch {
      // Ignore localStorage errors
    }
    return new Set();
  });
  const [crawlAllInProgress, setCrawlAllInProgress] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const pollingIntervalRef = useRef<number | null>(null);

  // Create source form state
  const [showCreateSourceForm, setShowCreateSourceForm] = useState(false);

  // Persist crawling state to localStorage
  useEffect(() => {
    try {
      if (crawlingSourceIds.size > 0) {
        const state: Record<string, number> = {};
        crawlingSourceIds.forEach(id => {
          state[id] = Date.now();
        });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Ignore localStorage errors
    }
  }, [crawlingSourceIds]);

  // Show toast notification
  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  // Start polling for crawl completion
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;
    pollingIntervalRef.current = window.setInterval(() => {
      reloadSources();
    }, 5000);
  }, [reloadSources]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Clean up polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Stop polling when no crawls in progress
  useEffect(() => {
    if (crawlingSourceIds.size === 0 && !crawlAllInProgress) {
      stopPolling();
    }
  }, [crawlingSourceIds.size, crawlAllInProgress, stopPolling]);

  // Handle single source crawl
  const handleCrawlSource = useCallback(async (sourceId: string, sourceName: string) => {
    setCrawlingSourceIds(prev => new Set(prev).add(sourceId));
    startPolling();

    try {
      const result = await triggerSourceCrawl(sourceId);
      showToast(result.message, 'success');

      // Remove from crawling set after a delay (async crawl continues in background)
      setTimeout(() => {
        setCrawlingSourceIds(prev => {
          const next = new Set(prev);
          next.delete(sourceId);
          return next;
        });
        reloadSources();
      }, 10000);
    } catch (error) {
      setCrawlingSourceIds(prev => {
        const next = new Set(prev);
        next.delete(sourceId);
        return next;
      });
      showToast(error instanceof Error ? error.message : 'Błąd pobierania', 'error');
    }
  }, [showToast, startPolling, reloadSources]);

  // Handle crawl all sources
  const handleCrawlAll = useCallback(async () => {
    const activeCount = sourcesData?.items.filter(s => s.isActive).length || 0;
    if (!window.confirm(`Czy na pewno chcesz uruchomić pobieranie dla ${activeCount} aktywnych źródeł?`)) {
      return;
    }

    setCrawlAllInProgress(true);
    startPolling();

    try {
      const result = await triggerAllCrawl();
      showToast(result.message, 'success');

      // Keep polling for a while to detect completion
      setTimeout(() => {
        setCrawlAllInProgress(false);
        reloadSources();
      }, 30000);
    } catch (error) {
      setCrawlAllInProgress(false);
      showToast(error instanceof Error ? error.message : 'Błąd pobierania', 'error');
    }
  }, [sourcesData, showToast, startPolling, reloadSources]);

  // Initialize from URL on mount
  useEffect(() => {
    const urlFilters = getFiltersFromUrl();
    setJobType(urlFilters.jobType);
    setStatus(urlFilters.status);
    setStartDate(urlFilters.startDate);
    setEndDate(urlFilters.endDate);
    setPage(urlFilters.page);
  }, []);

  // Update URL when filters change
  useEffect(() => {
    updateUrl({ jobType, status, startDate, endDate, page });
  }, [jobType, status, startDate, endDate, page]);

  // Build API filters
  const apiFilters: AdminFilters = {
    status: status || undefined,
    startDate: startDate ? new Date(startDate).toISOString() : undefined,
    endDate: endDate ? new Date(endDate + 'T23:59:59').toISOString() : undefined,
    page,
    limit: 50,
  };

  // Fetch data based on job type
  const {
    data: tenderLinksData,
    loading: tenderLinksLoading,
    error: tenderLinksError,
    reload: reloadTenderLinks,
  } = useTenderLinks(jobType === 'tender-links' ? apiFilters : { page: 1, limit: 1 });

  const {
    data: parcelsData,
    loading: parcelsLoading,
    error: parcelsError,
    reload: reloadParcels,
  } = useAdminParcels(jobType === 'parcels' ? apiFilters : { page: 1, limit: 1 });

  // Refresh all data
  const handleRefreshAll = useCallback(() => {
    reloadOverview();
    reloadSources();
    if (jobType === 'tender-links') {
      reloadTenderLinks();
    } else {
      reloadParcels();
    }
  }, [reloadOverview, reloadSources, reloadTenderLinks, reloadParcels, jobType]);

  // Reset filters
  const handleResetFilters = useCallback(() => {
    setStatus('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  }, []);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // Determine current data and loading state
  const isLoading = jobType === 'tender-links' ? tenderLinksLoading : parcelsLoading;
  const currentError = jobType === 'tender-links' ? tenderLinksError : parcelsError;

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <h1>Panel Administracyjny</h1>
        <div className="admin-actions">
          <button className="admin-button admin-button--primary" onClick={handleRefreshAll}>
            🔄 Odśwież wszystko
          </button>
          <a href="#/" className="admin-button">← Powrót do mapy</a>
        </div>
      </header>

      <main className="admin-main">
        {/* Overview Section */}
        <section className="admin-section">
          <h2>Status Systemu</h2>

          {overviewLoading && !overview && (
            <div className="loading-state">Wczytywanie statystyk...</div>
          )}

          {overviewError && (
            <div className="error-state">
              <p className="error-state__message">Błąd: {overviewError}</p>
              <button className="admin-button admin-button--primary" onClick={reloadOverview}>
                Spróbuj ponownie
              </button>
            </div>
          )}

          {overview && <OverviewPanel data={overview} />}
        </section>

        {/* Job History Section */}
        <section className="admin-section">
          <h2>Historia Operacji</h2>

          <FilterPanel
            jobType={jobType}
            status={status}
            startDate={startDate}
            endDate={endDate}
            onJobTypeChange={(type) => {
              setJobType(type);
              setPage(1);
            }}
            onStatusChange={(s) => {
              setStatus(s);
              setPage(1);
            }}
            onStartDateChange={(d) => {
              setStartDate(d);
              setPage(1);
            }}
            onEndDateChange={(d) => {
              setEndDate(d);
              setPage(1);
            }}
            onReset={handleResetFilters}
          />

          {currentError && (
            <div className="error-state">
              <p className="error-state__message">Błąd: {currentError}</p>
              <button
                className="admin-button admin-button--primary"
                onClick={jobType === 'tender-links' ? reloadTenderLinks : reloadParcels}
              >
                Spróbuj ponownie
              </button>
            </div>
          )}

          {jobType === 'tender-links' && tenderLinksData && (
            <TenderLinksTable
              data={tenderLinksData}
              loading={isLoading}
              onPageChange={handlePageChange}
              onRowClick={(item) => setSelectedTenderLinkId(item.id)}
            />
          )}

          {jobType === 'parcels' && parcelsData && (
            <ParcelsTable
              data={parcelsData}
              loading={isLoading}
              onPageChange={handlePageChange}
              onRowClick={(item) => setSelectedParcelId(item.id)}
            />
          )}

          {isLoading && !tenderLinksData && !parcelsData && (
            <div className="loading-state">Wczytywanie danych...</div>
          )}
        </section>

        {/* Sources Health Section */}
        <section className="admin-section">
          <div className="admin-section-header">
            <h2>Źródła danych</h2>
            <div className="admin-actions">
              <button
                className="admin-button admin-button--success"
                onClick={() => setShowCreateSourceForm(true)}
              >
                + Dodaj źródło
              </button>
              <button
                className={`admin-button admin-button--primary ${crawlAllInProgress ? 'admin-button--loading' : ''}`}
                onClick={handleCrawlAll}
                disabled={crawlAllInProgress || !sourcesData}
              >
                {crawlAllInProgress ? 'Pobieranie...' : 'Pobierz wszystkie'}
              </button>
            </div>
          </div>

          {sourcesError && (
            <div className="error-state">
              <p className="error-state__message">Błąd: {sourcesError}</p>
              <button className="admin-button admin-button--primary" onClick={reloadSources}>
                Spróbuj ponownie
              </button>
            </div>
          )}

          {sourcesLoading && !sourcesData && (
            <div className="loading-state">Wczytywanie źródeł...</div>
          )}

          {sourcesData && (
            <SourcesTable
              items={sourcesData.items}
              loading={sourcesLoading}
              crawlingSourceIds={crawlingSourceIds}
              togglingSourceIds={togglingSourceIds}
              onCrawl={handleCrawlSource}
              onToggle={toggleSource}
            />
          )}
        </section>

        {/* Toast Notification */}
        {toast && (
          <div className={`toast toast--${toast.type}`}>
            {toast.message}
          </div>
        )}
      </main>

      {/* Detail Modals */}
      {selectedTenderLinkId && (
        <DetailModal
          type="tender-link"
          data={tenderLinkDetail}
          loading={tenderLinkDetailLoading}
          error={tenderLinkDetailError}
          onClose={() => setSelectedTenderLinkId(null)}
        />
      )}

      {selectedParcelId && (
        <DetailModal
          type="parcel"
          data={parcelDetail}
          loading={parcelDetailLoading}
          error={parcelDetailError}
          onClose={() => setSelectedParcelId(null)}
        />
      )}

      {showCreateSourceForm && (
        <CreateSourceForm
          onClose={() => setShowCreateSourceForm(false)}
          onSuccess={() => {
            reloadSources();
            showToast('Źródło zostało dodane', 'success');
          }}
          onError={(message) => showToast(message, 'error')}
        />
      )}
    </div>
  );
}
